import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../../lib/api'
import { useAuthStore } from '../../../stores/auth'
import { Card } from '../../ui/Card'
import { Button } from '../../ui/Button'
import { Modal } from '../../ui/Modal'
import { ModalTransfer } from '../FormularTransfer'
import {
  EticheaStareTransfer,
  avertismentAnulare,
  confirmaStergere,
  type Transfer,
} from './stareTransfer'
import { eroare, faraAcces, MESAJ_FARA_SAMPLING } from './comun'

/**
 * Marfa adusă pentru activitate.
 *
 * Transferul scoate marfa din depozit în gestiunea de sampling; documentul pleacă în
 * ExpertAccounts, iar primirea se confirmă de aici (sau din Sampling → Transferuri) — aceleași
 * rute, aceleași butoane. Până pe 15.10.2026 primirea era doar acolo.
 */

function zi(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}

export function FilaTransferuri({ campanieId }: { campanieId: number }) {
  const qc = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const poateScrie = permissions.includes('marketing.sampling:write')
  const [transferNou, setTransferNou] = useState(false)
  const [retur, setRetur] = useState(false)
  const [legare, setLegare] = useState(false)
  /* Mutare făcută deja în ERP, notată la noi. Vezi `transferInregistrat.service` pe server. */
  const [inregistrare, setInregistrare] = useState(false)

  const q = useQuery<Transfer[]>({
    queryKey: ['sampling', 'transferuri', campanieId],
    queryFn: async () =>
      (await api.get<Transfer[]>('/marketing/sampling/transferuri', { params: { campanieId } }))
        .data,
  })
  const transferuri = q.data ?? []

  /*
    ANULAREA, si aici, nu doar in ecranul general de transferuri.

    Alexandru, 30.09.2026: „nu gasesc Anuleaza, nici Stergere". Se uita la fila asta — unde a si
    vazut ca marfa a plecat in gestiunea gresita —, iar butoanele erau pe alt ecran. Cine vede
    greseala trebuie sa o poata repara de unde o vede; altfel repararea depinde de cat de bine
    stie cineva unde mai sunt aceleasi randuri.

    Stergerea NU vine si ea: e alta fapta (curatenie in evidenta, dupa ce documentul a fost
    rezolvat in ERP) si cere alt drept. Ramane unde era.
  */
  const anuleaza = useMutation({
    mutationFn: async (id: number) =>
      (
        await api.post<{ stornoRefno: string | null; eaDocNo: string | null }>(
          `/marketing/sampling/transferuri/${id}/anuleaza`,
          {},
          { timeout: 120_000 },
        )
      ).data,
    onSuccess: (d) => {
      toast.success(
        d.stornoRefno
          ? `Anulat. Marfa s-a întors prin ${d.stornoRefno}${d.eaDocNo ? ` (${d.eaDocNo})` : ''}`
          : 'Transfer marcat anulat',
      )
      void qc.invalidateQueries({ queryKey: ['sampling', 'transferuri', campanieId] })
      /* Cererea se redeschide: restul urcă la loc, deci fila Necesar trebuie să se reîncarce. */
      void qc.invalidateQueries({ queryKey: ['necesar', campanieId] })
      void qc.invalidateQueries({ queryKey: ['campanie', campanieId] })
    },
    onError: eroare,
  })

  /*
    CORECTIA DESTINATIEI, doar in CRM.

    Alexandru, 30.09.2026: transferul plecase spre 351 in loc de 35, a indreptat documentul in
    ExpertAccounts, iar randul de la noi a ramas cu gestiunea veche. De acolo mai departe,
    gestiunea propusa la bonul de consum arata alta realitate decat ERP-ul.

    Nu emite niciun document si nu misca nimic — de aceea si textul spune limpede ce face si ce nu.
  */
  const gestiuni = useQuery({
    queryKey: ['gestiuni-corectie'],
    queryFn: async () =>
      (
        await api.get<{
          gestiuni: { id: number; nume: string }[]
        }>(`/marketing/costuri/${campanieId}/gestiune-consum`)
      ).data.gestiuni,
  })
  const corecteaza = useMutation({
    mutationFn: async (v: { id: number; locDestinatie: number }) =>
      api.patch(`/marketing/sampling/transferuri/${v.id}/destinatie`, {
        locDestinatie: v.locDestinatie,
      }),
    onSuccess: () => {
      toast.success('Destinația corectată în evidența CRM')
      void qc.invalidateQueries({ queryKey: ['sampling', 'transferuri', campanieId] })
      void qc.invalidateQueries({ queryKey: ['gestiune-consum', campanieId] })
    },
    onError: eroare,
  })

  /*
    STERGEREA, cu verificare. Alexandru, 30.09.2026: „adauga doar o confirmare de verificare, ca
    transferul trebuie sters in Expert".

    Nu sterge nimic in ExpertAccounts si nici nu poate: add-on-urile noastre scriu documente, nu
    le retrag. De aceea confirmarea cere NUMARUL documentului, tastat — singurul mod de a obliga
    pe cineva sa se uite acolo inainte, fiindca de acolo il ia.

    Cere dreptul de stergere, nu de scriere: e alta fapta decat anularea de deasupra.
  */
  const poateSterge = permissions.includes('marketing.sampling:delete')

  /*
    PRIMIREA in gestiune — documentul `tc-in` / `t-in` prin API, si verificarea in EA. Aceleasi
    rute ca in Sampling → Transferuri (`/primire`, `/primire/verifica`); nu se schimba nimic pe
    server, doar unde e butonul.
  */
  const primeste = useMutation({
    mutationFn: async (id: number) =>
      (
        await api.post<{
          ok: boolean
          docNo: string | null
          cuCost: number
          linii: number
          raspuns: string
        }>(`/marketing/sampling/transferuri/${id}/primire`, {}, { timeout: 120_000 })
      ).data,
    onSuccess: (r) => {
      if (r.ok)
        toast.success(
          `Primit în EA${r.docNo ? ` · ${r.docNo}` : ''} — ${r.cuCost} din ${r.linii} linii cu cost citit`,
        )
      else toast.error(r.raspuns, { duration: 20_000 })
      void qc.invalidateQueries({ queryKey: ['sampling'] })
      void qc.invalidateQueries({ queryKey: ['campanie', campanieId] })
    },
    onError: eroare,
  })
  const verifica = useMutation({
    mutationFn: async (id: number) =>
      (
        await api.post<{ confirmat: boolean; detaliu: string }>(
          `/marketing/sampling/transferuri/${id}/primire/verifica`,
          {},
          { timeout: 60_000 },
        )
      ).data,
    onSuccess: (r) => {
      if (r.confirmat) toast.success(r.detaliu)
      else toast(r.detaliu, { icon: '⏳' })
      void qc.invalidateQueries({ queryKey: ['sampling', 'transferuri', campanieId] })
    },
    onError: eroare,
  })
  const sterge = useMutation({
    mutationFn: async (id: number) => api.delete(`/marketing/sampling/transferuri/${id}`),
    onSuccess: () => {
      toast.success('Transfer șters din evidență')
      void qc.invalidateQueries({ queryKey: ['sampling', 'transferuri', campanieId] })
      void qc.invalidateQueries({ queryKey: ['necesar', campanieId] })
      void qc.invalidateQueries({ queryKey: ['campanie', campanieId] })
    },
    onError: eroare,
  })

  if (faraAcces(q.error))
    return (
      <Card title="Transferuri">
        <p className="text-sm text-b4l-text-muted">{MESAJ_FARA_SAMPLING}</p>
      </Card>
    )
  return (
    <div className="space-y-3">
      <Card
        title="Transferuri"
        actions={
          poateScrie ? (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setInregistrare(true)}>
                Înregistrează o mutare din EA
              </Button>
              <Button variant="secondary" onClick={() => setLegare(true)}>
                Leagă un transfer existent
              </Button>
              <Button variant="secondary" onClick={() => setRetur(true)}>
                Retur în depozit
              </Button>
              <Button onClick={() => setTransferNou(true)}>Transfer nou</Button>
            </div>
          ) : undefined
        }
      >
        {transferuri.length === 0 ? (
          <p className="text-sm text-b4l-text-muted">
            Niciun transfer pe activitatea asta. Transferurile făcute înainte de septembrie 2026 nu
            știu de ce activitate țin — dacă unul dintre ele a fost pentru aceasta, leagă-l cu
            butonul de sus.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-b4l-text-dim">
              <tr>
                <th className="py-2">Referință</th>
                <th>Când</th>
                {/*
                  LUCRUL PE CARE IL FACE UN TRANSFER nu se vedea nicaieri: tabelul avea referinta,
                  data, starea, documentul si liniile, dar nu si unde s-a mutat marfa.

                  Alexandru, 01.10.2026, cautand gestiunea gresita 351: „nu vad in interfata
                  nicaieri 351". Butonul „Corecteaza gestiunea" statea alaturi, cerandu-i sa repare
                  ceva ce nu putea citi.
                */}
                <th>Din → În</th>
                <th>Stare</th>
                <th>Document EA</th>
                <th className="text-right">Linii</th>
                {(poateScrie || poateSterge) && <th className="w-28"></th>}
              </tr>
            </thead>
            <tbody>
              {transferuri.flatMap((t) => [
                <tr key={t.id} className="border-t border-b4l-border align-top">
                  <td className="py-2 font-mono text-xs">{t.referinta}</td>
                  <td className="text-xs text-b4l-text-muted">{zi(t.createdAt)}</td>
                  <td className="text-xs">
                    <Gestiuni transfer={t} cunoscute={gestiuni.data} />
                  </td>
                  <td>
                    <EticheaStareTransfer transfer={t} />
                  </td>
                  <td className="text-xs">
                    {t.eaDocNo ?? '—'}
                    {t.tcInDocNo ? ` · primit ${t.tcInDocNo}` : ''}
                  </td>
                  <td className="pl-3 text-right text-xs">
                    {t.linii.map((l) => `${l.denumire} × ${l.cantitate}`).join(', ')}
                  </td>
                  {(poateScrie || poateSterge) && (
                    <td className="space-x-2 whitespace-nowrap pl-4 text-right">
                      {poateScrie && (t.status === 'trimis' || t.status === 'inregistrat') && (
                        <button
                          disabled={anuleaza.isPending}
                          onClick={() => {
                            if (window.confirm(avertismentAnulare(t))) anuleaza.mutate(t.id)
                          }}
                          className="text-xs text-b4l-amber-dark hover:underline disabled:opacity-40"
                          title="Emite transferul invers în EA și marchează rândul anulat"
                        >
                          {anuleaza.isPending ? 'Se anulează…' : 'Anulează'}
                        </button>
                      )}
                      {poateScrie && (t.status === 'trimis' || t.status === 'inregistrat') && (
                        <button
                          disabled={corecteaza.isPending}
                          onClick={() => {
                            const lista = gestiuni.data ?? []
                            const raspuns = window.prompt(
                              [
                                `Destinația lui ${t.referinta} e acum ${
                                  t.locDestinatieNume ?? t.locDestinatie
                                } (${t.locDestinatie}).`,
                                '',
                                'Corecția se face DOAR în CRM — documentul din ExpertAccounts nu',
                                'se atinge. Folosește-o doar dacă ai îndreptat deja mutarea acolo.',
                                '',
                                'Scrie numărul gestiunii corecte:',
                                ...lista.map((g) => `  ${g.id} — ${g.nume}`),
                              ].join('\n'),
                              '',
                            )
                            if (raspuns === null) return
                            const nou = Number(raspuns.trim())
                            if (!Number.isInteger(nou) || nou <= 0) {
                              toast.error('Scrie numărul gestiunii, din lista de mai sus.')
                              return
                            }
                            corecteaza.mutate({ id: t.id, locDestinatie: nou })
                          }}
                          className="text-xs text-b4l-blue hover:underline disabled:opacity-40"
                          title="Schimbă gestiunea destinație doar în evidența CRM"
                        >
                          Corectează gestiunea
                        </button>
                      )}
                      {poateSterge && (
                        <button
                          disabled={sterge.isPending}
                          onClick={() => {
                            if (confirmaStergere(t)) sterge.mutate(t.id)
                          }}
                          className="text-xs text-b4l-red hover:underline disabled:opacity-40"
                          title="Șterge din evidența CRM. Documentul din EA trebuie șters acolo."
                        >
                          Șterge
                        </button>
                      )}
                    </td>
                  )}
                </tr>,
                /* Primirea: doar pe transferurile plecate, neanulate si inca neprimite. */
                t.status === 'trimis' && (
                  <tr key={`${t.id}-primire`} className="align-top">
                    <td colSpan={7} className="pb-2.5">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        {t.tcInStatus === 'trimis' ? (
                          <span className="rounded-full bg-b4l-green-light px-2 py-0.5 text-b4l-green-dark">
                            Primită în gestiune{t.tcInDocNo ? ` · ${t.tcInDocNo}` : ''}
                          </span>
                        ) : (
                          <>
                            <span className="rounded-full bg-b4l-amber-light px-2 py-0.5 text-b4l-amber-dark">
                              Marfa a plecat, dar nu e primită în gestiune
                            </span>
                            {poateScrie && (
                              <Button
                                onClick={() => primeste.mutate(t.id)}
                                disabled={primeste.isPending}
                                className="!px-2 !py-1 !text-xs"
                              >
                                {primeste.isPending && primeste.variables === t.id
                                  ? 'Se trimite…'
                                  : `Primește (trimite ${t.doctype === 't-out' ? 't-in' : 'tc-in'})`}
                              </Button>
                            )}
                            {poateScrie && (
                              <Button
                                variant="secondary"
                                onClick={() => verifica.mutate(t.id)}
                                disabled={verifica.isPending}
                                className="!px-2 !py-1 !text-xs"
                              >
                                {verifica.isPending && verifica.variables === t.id
                                  ? 'Verific…'
                                  : 'Verifică în EA'}
                              </Button>
                            )}
                            {t.linkPrimireEa && (
                              <a
                                href={t.linkPrimireEa}
                                target="_blank"
                                rel="noreferrer"
                                className="text-b4l-text-muted underline"
                                title="Se deschide «Documente noi» în EA. De acolo: Transfer confirmat-Primire → Încarcă lista → Primește livrarea"
                              >
                                sau manual în EA
                              </a>
                            )}
                            {t.tcInStatus === 'esuat' && t.tcInRaspuns && (
                              <span className="text-b4l-red-dark" title={t.tcInRaspuns}>
                                ultima încercare n-a mers — ține mouse-ul aici pentru motiv
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ),
              ])}
            </tbody>
          </table>
        )}
        <p className="mt-3 text-xs text-b4l-text-dim">
          Până la primire, marfa a ieșit din depozit, dar n-a intrat în gestiunea de sampling.
          „Primește” trimite documentul de primire în ExpertAccounts; „Verifică în EA” doar întreabă
          dacă s-a primit deja acolo.
        </p>
      </Card>

      {transferNou && (
        <ModalTransfer
          campanieId={campanieId}
          onClose={() => {
            setTransferNou(false)
            void qc.invalidateQueries({ queryKey: ['sampling', 'transferuri', campanieId] })
          }}
        />
      )}

      {inregistrare && (
        <ModalTransfer
          campanieId={campanieId}
          inregistrare
          onClose={() => {
            setInregistrare(false)
            void qc.invalidateQueries({ queryKey: ['sampling', 'transferuri', campanieId] })
            void qc.invalidateQueries({ queryKey: ['campanie', campanieId] })
          }}
        />
      )}

      {retur && (
        <ModalTransfer
          campanieId={campanieId}
          retur
          onClose={() => {
            setRetur(false)
            void qc.invalidateQueries({ queryKey: ['sampling', 'transferuri', campanieId] })
          }}
        />
      )}

      {legare && (
        <ModalLegare
          campanieId={campanieId}
          onClose={() => setLegare(false)}
          onGata={() => {
            void qc.invalidateQueries({ queryKey: ['sampling', 'transferuri', campanieId] })
          }}
        />
      )}
    </div>
  )
}

/** Transferurile fără activitate — cele de dinainte de livrare, sau făcute din Acțiuni. */
function ModalLegare({
  campanieId,
  onClose,
  onGata,
}: {
  campanieId: number
  onClose: () => void
  onGata: () => void
}) {
  const q = useQuery<Transfer[]>({
    queryKey: ['sampling', 'transferuri', 'fara-activitate'],
    queryFn: async () => (await api.get<Transfer[]>('/marketing/sampling/transferuri')).data,
  })
  const libere = (q.data ?? []).filter((t) => t.campanieId == null)

  const leaga = useMutation({
    mutationFn: async (id: number) =>
      (await api.patch(`/marketing/sampling/transferuri/${id}/activitate`, { campanieId })).data,
    onSuccess: () => {
      toast.success('Transfer legat de activitate')
      onGata()
      onClose()
    },
    onError: eroare,
  })

  return (
    <Modal open onClose={onClose} title="Leagă un transfer existent">
      {libere.length === 0 ? (
        <p className="text-sm text-b4l-text-muted">Nu există transferuri nelegate.</p>
      ) : (
        <ul className="divide-y divide-b4l-border text-sm">
          {libere.map((t) => (
            <li key={t.id} className="flex items-center gap-3 py-2">
              <span className="font-mono text-xs">{t.referinta}</span>
              <span className="text-xs text-b4l-text-muted">{zi(t.createdAt)}</span>
              <span className="flex-1 text-xs text-b4l-text-muted">
                {t.linii.map((l) => `${l.denumire} × ${l.cantitate}`).join(', ')}
              </span>
              <Button
                className="!px-2 !py-1 !text-xs"
                disabled={leaga.isPending}
                onClick={() => leaga.mutate(t.id)}
              >
                Leagă
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}

/**
 * De unde și unde a plecat marfa.
 *
 * Numele gestiunii, cu id-ul alături: „Dorian Ion (35)". Id-ul nu e decor — el e cel scris în
 * document și tot el e cel care se greșește la tastare, cum s-a întâmplat pe 30.09.2026 cu `351`
 * în loc de `35`.
 *
 * O gestiune care nu e printre cele cu stoc active se scrie pe roșu. Exact ăsta era cazul lui
 * `351`: marfa figura mutată undeva ce nu există, iar raportul pe agent n-o găsea — dar nimic de
 * pe ecran n-o spunea.
 *
 * Roșul e un SEMN DE VERIFICAT, nu un verdict: lista cu care se compară e cea a gestiunilor cu
 * stoc active, deci una dezactivată între timp ar apărea și ea roșie. De aceea textul din tooltip
 * spune „verifică", nu „nu există".
 *
 * Când destinația a fost corectată, se vede și cea veche, tăiată: corecția rămâne o urmă, nu o
 * rescriere a istoriei.
 */
function Gestiuni({
  transfer,
  cunoscute,
}: {
  transfer: Transfer
  /* Aceeasi lista pe care o foloseste si butonul de corectie — nu o a doua interogare. */
  cunoscute: { id: number; nume: string }[] | undefined
}) {
  const ids = new Set((cunoscute ?? []).map((g) => g.id))

  const loc = (id: number, nume: string | null | undefined) => {
    const necunoscuta = (cunoscute?.length ?? 0) > 0 && !ids.has(id)
    return (
      <span
        className={necunoscuta ? 'text-b4l-red' : undefined}
        title={
          necunoscuta
            ? 'Nu e printre gestiunile cu stoc active. Verifică dacă id-ul e corect.'
            : undefined
        }
      >
        {nume ?? '—'} ({id})
      </span>
    )
  }

  return (
    <span className="whitespace-nowrap">
      {loc(transfer.locSursa, transfer.locSursaNume)}
      <span className="mx-1 text-b4l-text-dim">→</span>
      {loc(transfer.locDestinatie, transfer.locDestinatieNume)}
      {transfer.destinatieVeche != null && (
        <span className="block text-[11px] text-b4l-text-muted">
          corectat din <s>{transfer.destinatieVeche}</s>
          {transfer.destinatieCorectataDeNume ? ` · ${transfer.destinatieCorectataDeNume}` : ''}
        </span>
      )}
    </span>
  )
}
